'use client';

import { ReactNode, createContext, useContext } from 'react';
import { Controller } from 'react-hook-form';
import * as RadioGroup from '@/shared/ui/radio-group';
import { FormFieldProps } from './types';
import { mergeRefs } from './utils/mergeRefs';
import { parseRulesForInputAttributes } from './utils/parseRulesForInputAttributes';

type Props = Omit<FormFieldProps<typeof RadioGroup.Item>, 'children' | 'name'> & {
    children?: ReactNode;
};

type RootContextValue = {
    name: string;
    allowDeselect?: boolean;
    onDeselect?: () => void;
};

export const RadioGroupRootContext = createContext<RootContextValue>({} as RootContextValue);

const FormRadioGroupRootComponent = ({
    name,
    control,
    defaultValue,
    disabled,
    rules,
    shouldUnregister,
    children,
    asChild,
    onValueChange,
    allowDeselect,
    ...props
}: Omit<FormFieldProps<typeof RadioGroup.Root>, 'children' | 'onValueChange'> & {
    children?: ReactNode | (({ value }: { value: string }) => ReactNode);
    asChild?: boolean;
    allowDeselect?: boolean;
    onValueChange?: (value: string | undefined) => void;
}) => {
    return (
        <Controller
            {...{ name, control, defaultValue, disabled, rules, shouldUnregister }}
            render={({ field }) => {
                return (
                    <RadioGroupRootContext.Provider
                        value={{
                            name,
                            allowDeselect,
                            onDeselect: () => {
                                // '' is the unset single-pick value. undefined would leave the controlled group looking selected.
                                field.onChange('');
                                onValueChange?.('');
                            },
                        }}
                    >
                        <RadioGroup.Root
                            name={name}
                            value={field.value ?? ''}
                            onValueChange={(value) => {
                                field.onChange(value);
                                onValueChange?.(value);
                            }}
                            asChild={asChild}
                            {...props}
                        >
                            {typeof children === 'function' ? children({ value: field.value }) : children}
                        </RadioGroup.Root>
                    </RadioGroupRootContext.Provider>
                );
            }}
        />
    );
};

const FormRadioGroupItemComponent = ({
    id,
    control,
    defaultValue,
    disabled,
    rules,
    shouldUnregister,
    ref,
    children,
    asChild,
    ...props
}: Props & { asChild?: boolean }) => {
    const { name, allowDeselect, onDeselect } = useContext(RadioGroupRootContext);
    return (
        <Controller
            {...{ name, control, defaultValue, disabled, rules, shouldUnregister }}
            render={({ field: { ref: fieldRef, value: fieldValue, onChange, ...field } }) => {
                const isChecked = fieldValue === props.value;
                return (
                    <RadioGroup.Item
                        ref={mergeRefs([fieldRef, ref])}
                        id={id || `${name}-${props.value}`}
                        {...parseRulesForInputAttributes(rules)}
                        {...field}
                        onClick={(e) => {
                            // Pokud je allowDeselect zapnuté a klikneme na již vybranou položku
                            if (allowDeselect && isChecked && props.value !== undefined) {
                                e.preventDefault();
                                e.stopPropagation();
                                onChange('');
                                onDeselect?.();
                            }
                        }}
                        asChild={asChild}
                        {...props}
                    >
                        {children}
                    </RadioGroup.Item>
                );
            }}
        />
    );
};

export const FormRadioGroup = Object.assign(FormRadioGroupRootComponent, {
    Item: FormRadioGroupItemComponent,
    Indicator: RadioGroup.Indicator,
});
